/**
 * Single source of truth for "how far is this trainer from being live?".
 *
 * Two surfaces read from here and must never disagree:
 *   - PendingApprovalBanner  — the full yellow banner, shown only on the
 *     trainer's own listing page and inside the Edit Listing wizard.
 *   - TrainerStatusStrip     — the one-line sitewide strip, shown on every
 *     other page.
 *
 * Going live needs two independent things:
 *   1. an active subscription on a tier that includes a live profile
 *      (Tier 2 or Tier 3 — Tier 1 never goes live), and
 *   2. `metadata.isVerified`, a persisted flag an admin sets by hand after
 *      confirming the trainer over Instagram. It is a remembered fact of its
 *      own, deliberately NOT inferred from the listing state.
 *
 * Setting `isVerified` stays a manual admin action. In Sharetribe Console:
 * Manage -> Listings -> open the listing card -> Metadata -> Edit ->
 * "+ Add new field", Key `isVerified`, Type Boolean, Value true. Nothing in the
 * app writes this flag; both surfaces only read it.
 */

// Resolved states. The numbering matches the states in the spec so the copy,
// the tests and the ticket all line up.
export const TRAINER_STATE_NOT_SUBSCRIBED = 1; // not subscribed, not verified
export const TRAINER_STATE_UNVERIFIED = 2; // subscribed (Tier 2/3), not verified
export const TRAINER_STATE_UNVERIFIED_TIER1 = '2a'; // subscribed (Tier 1), not verified
export const TRAINER_STATE_VERIFIED_TIER1 = 3; // subscribed (Tier 1), verified
export const TRAINER_STATE_LIVE = 4; // subscribed (Tier 2/3), verified — nothing to show
export const TRAINER_STATE_VERIFIED_NO_PLAN = 5; // verified, no subscription

export const TIER_1 = 'Tier 1';

// States the banner has nothing useful to say about. Its copy is all about
// getting a profile live, which does not apply to a listing the trainer has not
// finished writing or one that has been taken down. The listing page action bar
// already covers both, with their own wording and a "finish listing" link.
const STATES_WITHOUT_A_STATUS_MESSAGE = ['draft', 'closed'];

// A subscription counts as active while Stripe reports it active, and also
// while a change is still settling ("pending"), so the surfaces don't flicker
// back to "get a plan" mid-payment. Listings the admin comped (`isFreeListing`)
// behave like a paid Tier 2/3 subscription.
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'pending'];

/**
 * Resolves a trainer's live status from their profile listing.
 *
 * @param {Object} listing - the trainer's own listing (denormalised, or the
 *   plain `{ metadata }` shape the Topbar keeps in the user duck)
 * @returns {Object|null} `{ isSubscribed, isVerified, tier, isTier1, state }`,
 *   or `null` when there is no listing to judge.
 */
export const getTrainerLiveStatus = listing => {
  const metadata = listing?.attributes?.metadata || listing?.metadata;
  if (!metadata) {
    return null;
  }

  const { subscriptionStatus, subscriptionName, isFreeListing } = metadata;

  // `subscriptionStatus` alone decides this, NOT the presence of a Stripe
  // subscription id: an admin can grant a subscription from the plans page,
  // which writes an active status and a tier but no Stripe id at all. Requiring
  // the id made every admin-granted listing read as "not subscribed".
  //
  // Any tier counts, Tier 1 included — being on Tier 1 is a tier problem, not a
  // subscription problem, and the copy treats the two separately.
  const isSubscribed =
    !!isFreeListing || ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus);
  const isVerified = metadata.isVerified === true;
  const tier = isFreeListing && !subscriptionName ? null : subscriptionName || null;
  // A comped listing is live-capable, so it is never treated as Tier 1.
  const isTier1 = !isFreeListing && tier === TIER_1;

  let state;
  if (!isSubscribed) {
    state = isVerified ? TRAINER_STATE_VERIFIED_NO_PLAN : TRAINER_STATE_NOT_SUBSCRIBED;
  } else if (!isVerified) {
    state = isTier1 ? TRAINER_STATE_UNVERIFIED_TIER1 : TRAINER_STATE_UNVERIFIED;
  } else {
    state = isTier1 ? TRAINER_STATE_VERIFIED_TIER1 : TRAINER_STATE_LIVE;
  }

  return { isSubscribed, isVerified, tier, isTier1, state };
};

/**
 * True when there is something for the banner or the strip to say. Once a
 * trainer is verified on Tier 2/3 both surfaces disappear on their own.
 */
export const shouldShowTrainerStatus = status =>
  !!status && status.state !== TRAINER_STATE_LIVE;

/**
 * Whether the full banner should render for a listing its owner is viewing.
 *
 * Combines the resolved status with the listing state, so both listing page
 * layouts ask the same question and cannot drift apart. While this is true the
 * banner carries the "Back to editing" link, so the default own-listing action
 * bar stands down and the trainer sees a single prompt rather than two.
 */
export const shouldShowTrainerStatusForListing = listing => {
  const state = listing?.attributes?.state;
  if (STATES_WITHOUT_A_STATUS_MESSAGE.includes(state)) {
    return false;
  }
  return shouldShowTrainerStatus(getTrainerLiveStatus(listing));
};

/**
 * Which action the call-to-action performs for a given state:
 *   'plan'      — go to the plans page and buy a subscription
 *   'instagram' — open the Simply Fit Instagram in a new tab to get verified
 *   'upgrade'   — change an existing Tier 1 subscription up to Tier 2
 */
export const getTrainerStatusAction = status => {
  switch (status?.state) {
    case TRAINER_STATE_NOT_SUBSCRIBED:
    case TRAINER_STATE_VERIFIED_NO_PLAN:
      return 'plan';
    case TRAINER_STATE_UNVERIFIED:
    case TRAINER_STATE_UNVERIFIED_TIER1:
      return 'instagram';
    case TRAINER_STATE_VERIFIED_TIER1:
      return 'upgrade';
    default:
      return null;
  }
};

/**
 * Translation-key suffix for a state, so the banner and the strip pick their
 * copy the same way (`PendingApprovalBanner.heading_state1`,
 * `TrainerStatusStrip.text_state1`, ...).
 */
export const getTrainerStatusKey = status =>
  status?.state != null ? `state${status.state}` : null;

export const SIMPLY_FIT_INSTAGRAM_URL = 'https://www.instagram.com/simplyfit.io/';

/**
 * True when the listing sits on a plan that can actually put a profile live:
 * an active (or still settling) subscription on Tier 2/3, or a comped
 * `isFreeListing`.
 *
 * Tier 1 is deliberately false. It is a paid plan, but it never publishes a
 * profile, so a Tier 1 trainer still needs to be pointed at the plans page —
 * the same rule the listing page's action bar has always used.
 *
 * @param {Object} listing - the trainer's own listing
 * @returns {boolean} whether the listing can be live on its current plan
 */
export const hasLiveCapableSubscription = listing => {
  const status = getTrainerLiveStatus(listing);
  return !!status && status.isSubscribed && !status.isTier1;
};
